/**
 *
 * DocumentsDetailPage
 *
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { stringify, useQueryParams } from '@domonda/query-params';
import { history } from 'lib/history';
import { DOCUMENTS_PAGE } from 'lib/routes';
import { filterCache, invoiceCache } from '../../../../../filterCache';

// api
import { getToken } from 'lib/api/auth';
import {
  getInvoiceContainer,
  lockInvoiceContainer,
  keepAliveInvoiceContainer,
  unlockInvoiceContainer,
  saveInvoiceContainer,
  deleteInvoiceContainer,
  GetInvoiceContainerResponse,
} from 'lib/api/invoiceContainer';
import {
  saveInvoice,
  deleteInvoice,
  SaveInvoiceResponse,
  DeleteInvoiceResponse,
  SaveInvoiceParams,
  DeleteInvoiceParams,
} from 'lib/api/invoice';

// resource
import { useResourceState, useCreatedResource, useAsyncEffect } from 'lib/domonda-resource/react';

// ui
import { Err, ErrBoundry, Grid } from '@domonda/ui';

// modules
import { InvoiceDetail } from 'modules/InvoiceDetail';
import { documentsOverviewResource } from 'modules/DocumentsOverview';
import { DocumentNavigation, DocumentSplitter } from 'modules/Document';

// decorate
import { decorate, Decorate } from './decorate';
import { getOcrResult } from 'lib/api/ocrResult';
import { getConfigFromStore } from 'lib/api/customerConfig';
import { CircularLoading } from 'modules/Loading/CircularLoading';
import { getInvoiceDetailTypeFieldType } from 'lib/api/flatInvoiceDetails';
import { convertToUSStandard } from 'lib/numberHelpers';

export type DocumentsDetailPageProps = RouteComponentProps<{ id: string }>;

interface DocumentsDetailPageQueryParams {
  invoiceIndex: number;
}

const KEEP_ALIVE_TIMEOUT_SECONDS = 4 * 60;

const DocumentsDetailPage: React.FC<DocumentsDetailPageProps & Decorate> = (props) => {
  const { match, classes } = props;
  const { params } = match;

  const customerConfig = getConfigFromStore();
  const removeGuiSplittingArea = customerConfig ? customerConfig.RemoveGuiSplittingArea : false;
  const [splitMode, setSplitMode] = useState(false);

  const [{ data, error }, resource] = useCreatedResource(getInvoiceContainer, params, [params.id]);
  const accessToken = getToken();
  const container = data && data.InvoiceContainers[0];
  const containerId = container && container.ContainerId;

  const { data: documentsOverviewData, params: documentsOverviewParams } = useResourceState(
    documentsOverviewResource,
  );

  const { prevContainerUrl, nextContainerUrl, closeContainerUrl } = useMemo(() => {
    const containers = documentsOverviewData && documentsOverviewData.InvoiceContainers;
    const containersIds =
      containers && containers.map(({ ContainerId }: { ContainerId: any }) => ContainerId);

    let prevContainerUrl = '';
    let nextContainerUrl = '';
    const closeContainerUrl = `${DOCUMENTS_PAGE}/?${stringify(documentsOverviewParams)}`;

    if (containerId && containersIds) {
      for (let i = 0; i < containersIds.length; i++) {
        if (containersIds[i + 1] === containerId)
          prevContainerUrl = `${DOCUMENTS_PAGE}/${containersIds[i]}`;
        if (containersIds[i - 1] === containerId)
          nextContainerUrl = `${DOCUMENTS_PAGE}/${containersIds[i]}`;
      }
    }
    return { prevContainerUrl, nextContainerUrl, closeContainerUrl };
  }, [data, documentsOverviewData, documentsOverviewParams]);

  const cacheNextPrev = async (containerUrl: string) => {
    if (containerUrl) {
      const id = containerUrl.split('/')[2];
      const containerParams = { id };
      const container: GetInvoiceContainerResponse = await getInvoiceContainer(containerParams);
      container.InvoiceContainers[0].Invoices.map(async (invoice) => {
        await getOcrResult({
          ContainerId: id,
          InvoiceId: invoice.InvoiceId,
        });
      });
    }
  };

  // direction : 1-Previous , 2-Next
  const calcContainers = (containerUrl: string, direction = 0) => {
    if (containerUrl) {
      const contId = containerUrl.split('/')[2];
      const containers = documentsOverviewData && documentsOverviewData.InvoiceContainers;
      const containersIds =
        containers && containers.map(({ ContainerId }: { ContainerId: any }) => ContainerId);
      let retContainer = '';
      if (contId && containersIds) {
        for (let i = 0; i < containersIds.length; i++) {
          if (direction === 1 && containersIds[i + 1] === contId)
            retContainer = `${DOCUMENTS_PAGE}/${containersIds[i]}`;
          if (direction === 2 && containersIds[i - 1] === contId)
            retContainer = `${DOCUMENTS_PAGE}/${containersIds[i]}`;
        }
      }
      return retContainer;
    }
    return '';
  };

  useEffect(() => {
    async function cacheData() {
      await cacheNextPrev(prevContainerUrl);
      await cacheNextPrev(calcContainers(prevContainerUrl, 1));
      await cacheNextPrev(nextContainerUrl);
      await cacheNextPrev(calcContainers(nextContainerUrl, 2));
    }
    cacheData();
  }, [prevContainerUrl, nextContainerUrl]);

  const [queryParams, setQueryParams] = useQueryParams<DocumentsDetailPageQueryParams>(
    {
      invoiceIndex: {
        type: 'number',
        defaultValue: 0,
      },
    },
    { onPathname: match.url },
  );

  const [lockError, setLockError] = useState<Error | null>(null);
  useAsyncEffect(async () => {
    if (!containerId || lockError) {
      return;
    }

    try {
      await lockInvoiceContainer({
        ContainerId: containerId,
      });
    } catch (error) {
      setLockError(error);
      return;
    }

    const keepAliveInterval = setInterval(async () => {
      try {
        await keepAliveInvoiceContainer({
          ContainerId: containerId,
        });
      } catch (error) {
        setLockError(error);
      }
    }, KEEP_ALIVE_TIMEOUT_SECONDS * 1000);

    return () => {
      clearInterval(keepAliveInterval);
      unlockInvoiceContainer({
        AccessToken: accessToken,
        ContainerId: containerId,
      });
    };
  }, [containerId, lockError]);

  const handleInvoiceSaveAndDelete = (response: SaveInvoiceResponse | DeleteInvoiceResponse) => {
    const currentContainer = response.InvoiceContainers[0];
    if (
      !currentContainer.Invoices ||
      !currentContainer.Invoices.length ||
      currentContainer.Invoices[0].InvoiceState === 'Deleted' ||
      currentContainer.Invoices[0].InvoiceState === 'Released'
    ) {
      documentsOverviewResource.updateData(
        (data) =>
          data && {
            ...data,
            InvoiceContainers: data.InvoiceContainers.filter(
              ({ ContainerId }: { ContainerId: any }) =>
                ContainerId !== currentContainer.ContainerId,
            ),
          },
      );

      if (nextContainerUrl) {
        history.push(nextContainerUrl);
        return;
      }

      history.push(closeContainerUrl);
      return;
    }

    resource.updateData(() => ({
      State: response.State,
      InvoiceContainers: [...response.InvoiceContainers],
    }));
  };

  const removeInvoinceFromCache = (params: SaveInvoiceParams | DeleteInvoiceParams) => {
    filterCache.getCache().map((key: string) => {
      const value = filterCache.get(key);
      for (let i = 0; i < value.InvoiceContainers.length; i++) {
        if (value.InvoiceContainers[i].ContainerId === params.ContainerId) {
          value.InvoiceContainers.splice(i, 1);
        }
      }
    });
    invoiceCache.getCache().map((key: string) => {
      const value = invoiceCache.get(key);
      if (value.InvoiceContainers[0].ContainerId === params.ContainerId) invoiceCache.del(key);
    });
  };

  const calculateVatAndNetTotal = (params: SaveInvoiceParams) => {
    let newVatTotal = 0;
    let newNetTotal = 0;
    params.Invoice.DetectInvoiceResponse.PredictionGroups.forEach((group) => {
      if (group.TypeName === 'VatGroup') {
        group.InvoiceDetailTypePredictions.forEach((element) => {
          if (element.TypeName === 'VatAmount' && element.Value)
            newVatTotal += Number(element.Value);
          else if (element.TypeName === 'NetAmount' && element.Value)
            newNetTotal += Number(element.Value);
        });
      }
    });
    params.Invoice.DetectInvoiceResponse.InvoiceDetailTypePredictions.forEach((element) => {
      if (element.TypeName === 'VatTotalAmount') element.Value = newVatTotal.toFixed(2);
      else if (element.TypeName === 'NetTotalAmount') element.Value = newNetTotal.toFixed(2);
    });
  };

  const handleInvoiceSave = async (params: SaveInvoiceParams) => {
    //Before deleting copy dictionary
    (params as any).AdditionalProperties = params.Invoice.DetectInvoiceResponse.AdditionalProperties;
    (params as any)["ClientId"] = params.Invoice.DetectInvoiceResponse.Client!.Id;
    (params as any)["ReceiverEmail"] = params.Invoice.DetectInvoiceResponse.AdditionalProperties!['ReceiverEmail'];
    (params as any)["SenderEmail"] = params.Invoice.DetectInvoiceResponse.AdditionalProperties!['SenderEmail'];
    
    delete params.Invoice.DetectInvoiceResponse.Client;
    delete params.Invoice.DetectInvoiceResponse.Supplier;
    delete params.Invoice.DetectInvoiceResponse.AdditionalProperties;

    // remove OCR result when saving invoice
    delete params.Invoice.DetectInvoiceResponse.OcrResult;
    
    const convertToISODate = (date: any) => {
      date = date.toISOString().split('T')[0];
      return date;
    };

    params.Invoice.DetectInvoiceResponse.InvoiceDetailTypePredictions.forEach((element) => {
      //We for now only return Invoice or CreditMemo after validation, everything else will be defaulted to 'Invoice'
      if (
        element.TypeName === 'DocumentType' &&
        element.Value !== 'Invoice' &&
        element.Value !== 'CreditMemo'
      ) {
        element.Value = 'Invoice';
      }

      if (getInvoiceDetailTypeFieldType(element.TypeName) === 'number') {
        if (!element.Value) element.Value = '';
        else {
          let convertedValue = convertToUSStandard(element.Value).replace(',', '');
          convertedValue = Number(convertedValue).toFixed(2);
          element.Value = convertedValue;
        }
      }

      if (element.TypeName === 'InvoiceDate' && typeof element.Value === 'object' && typeof element['Value']['getMonth'] === 'function') { // if it's a date object convert it to ISO date (YYYY-MM-DD);
        let properDate = convertToISODate(element.Value);
        element.Value = properDate;
      }

      // Set date to empty string if it's null.
      // Date fields are null when deleting value,
      // api is complaining on null. This is a fix.
      if (
        element.TypeName === 'InvoiceDate' ||
        element.TypeName === 'SenderOrderDate' ||
        element.TypeName === 'ReceiverOrderDate' ||
        element.TypeName === 'DeliveryDate'
      ) {
        if (element.Value === null) {
          element.Value = '';
        }
      }
    });
    params.Invoice.DetectInvoiceResponse.PredictionGroups.forEach((group) => {
      group.InvoiceDetailTypePredictions.forEach((item) => {
        if (getInvoiceDetailTypeFieldType(item.TypeName) === 'number') {
          if (!item.Value) item.Value = '';
          else {
            let convertedValue = convertToUSStandard(item.Value).replace(',', '');
            convertedValue = Number(convertedValue).toFixed(2);
            item.Value = convertedValue;
          }
        }
      });
    });
    calculateVatAndNetTotal(params);
    delete params.BadClientId;
    delete params.BadSupplierId;
    removeInvoinceFromCache(params);
    const response = await saveInvoice(params);
    handleInvoiceSaveAndDelete(response);
  };

  if (lockError) {
    return <Err error={lockError} onRetry={() => setLockError(null)} />;
  }

  if (error) {
    return <Err error={error} onRetry={resource.retry} />;
  }

  if (!data) {
    return <CircularLoading />;
  }

  if (!container) {
    return <Err error={new Error('Document does not exist.')} onRetry={resource.retry} />;
  }

  if (!container.Invoices || !container.Invoices.length) {
    return (
      <Err error={new Error('Document does not have any invoices.')} onRetry={resource.retry} />
    );
  }

  if (queryParams.invoiceIndex >= container.Invoices.length) {
    if (queryParams.invoiceIndex > 1) {
      return (
        <Redirect
          to={`${match.url}?${stringify({ invoiceIndex: queryParams.invoiceIndex - 1 })}`}
        />
      );
    }
    return <Redirect to={match.url} />;
  }

  if (splitMode && !removeGuiSplittingArea) {
    return (
      <ErrBoundry>
        <DocumentSplitter
          invoices={container.Invoices}
          onDeleteContainer={async () => {
            await deleteInvoiceContainer({ ContainerId: container.ContainerId });
            documentsOverviewResource.fetch();
            history.push(DOCUMENTS_PAGE);
          }}
          onCancelSplit={() => setSplitMode(false)}
          onApplySplit={async (invoices) => {
            const response = await saveInvoiceContainer({
              ...container,
              Invoices: invoices.map((invoice) => ({
                InvoiceId: invoice.InvoiceId,
                Pages: invoice.Pages.map((page) => ({
                  PageNo: page.PageNo,
                  Deleted: page.Deleted,
                })),
              })),
            });
            resource.updateData(() => ({
              State: response.State,
              InvoiceContainers: [...response.InvoiceContainers],
            }));
            documentsOverviewResource.fetch();
            setSplitMode(false);
          }}
        />
      </ErrBoundry>
    );
  }
  const width = removeGuiSplittingArea ? '0 35fr' : '225px 1fr';
  const temp = `
 'navigation invoice' 100%
 / ${width}`;
  return (
    <ErrBoundry>
      <Grid
        fill
        container
        template={temp} /// 225px 1fr`}
        className={classes.grid}
      >
        {!removeGuiSplittingArea && (
          <Grid area="navigation" className={classes.navigation}>
            <DocumentNavigation
              onActivateSplit={() => setSplitMode(true)}
              invoices={container.Invoices}
              selectedInvoiceIndex={queryParams.invoiceIndex}
              onChangeSelectedInvoiceIndex={(invoiceIndex) => setQueryParams({ invoiceIndex })}
            />
          </Grid>
        )}

        <Grid area="invoice" className={classes.invoice}>
          <InvoiceDetail
            key={`${params.id}-invoice-${queryParams.invoiceIndex}`}
            containerId={params.id}
            invoice={container.Invoices[queryParams.invoiceIndex]}
            SupplierResult={container.Invoices[queryParams.invoiceIndex].SupplierResult}
            prevContainerUrl={prevContainerUrl}
            nextContainerUrl={nextContainerUrl}
            badClientId={container.Invoices[0].BadClientId}
            badSupplierId={container.Invoices[0].BadSupplierId}
            closeContainerUrl={closeContainerUrl}
            onDeleteInvoice={async (params) => {
              removeInvoinceFromCache(params);
              const response = await deleteInvoice(params);
              handleInvoiceSaveAndDelete(response);
            }}
            onSaveInvoice={handleInvoiceSave}
          />
        </Grid>
      </Grid>
    </ErrBoundry>
  );
};

const StyledDocumentsDetailPage = decorate(DocumentsDetailPage);
export { StyledDocumentsDetailPage as DocumentsDetailPage };
